/* global SideDrawer */

class CartDrawer extends SideDrawer {
  constructor() {
    super();
    this.isCheckingGiftEligibility = false; // Prevent infinite loops
    this.hasTriedGiftRefresh = false; // Prevent multiple gift refresh attempts
  }

  connectedCallback() {
    this.bindEvents();
    this.addGiftProductStyles();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    this.cartChangeHandler = this.handleCartChange.bind(this);
    
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    
    // Listen for cart changes from main.js CartForm class
    document.addEventListener('on:cart:change', this.cartChangeHandler);
    
    // Listen for when products are added to cart (immediate response)
    document.addEventListener('on:cart:add', () => {
      // Skip if we're on cart page
      if (window.location.pathname === '/cart') {
        return;
      }

      // Update cart count and check gift eligibility immediately
      this.updateCartCount();
      
      setTimeout(() => {
        this.checkGiftEligibility();
      }, 100);
    });
    
    // Listen for quantity changes and removals in cart
    this.addEventListener('change', (event) => {
      // Skip if we're on cart page
      if (window.location.pathname === '/cart') {
        return;
      }

      if (event.target.classList.contains('cart-item__quantity-input')) {
        setTimeout(() => {
          this.checkGiftEligibility();
        }, 300);
      }
    });
    
    // Listen for remove item clicks
    this.addEventListener('click', (event) => {
      // Skip if we're on cart page
      if (window.location.pathname === '/cart') {
        return;
      }

      if (event.target.classList.contains('cart-item__remove') || 
          event.target.closest('.cart-item__remove')) {
        setTimeout(() => {
          this.checkGiftEligibility();
        }, 500);
      }
    });
    
    // Listen for when drawer closes to do final cleanup
    this.addEventListener('on:side-drawer:after-close', () => {
      // Skip if we're on cart page
      if (window.location.pathname === '/cart') {
        return;
      }

      setTimeout(() => {
        this.checkGiftEligibility();
      }, 300);
    });
    
    // Check eligibility when drawer opens to sync with cart page
    this.addEventListener('on:side-drawer:after-open', () => {
      // Skip if we're on cart page
      if (window.location.pathname === '/cart') {
        return;
      }

      setTimeout(() => {
        this.checkGiftEligibility();
      }, 500);
    });
    
    this.addEventListener('on:cart-drawer:before-open', () => {
      // Skip gift eligibility check if we're on cart page
      if (window.location.pathname === '/cart') {
        // Still load images and initialize components, but skip gift check
        theme.manuallyLoadImages(this);
        this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
        return;
      }

      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      // Check gift eligibility when opening drawer - no delay needed
      this.checkGiftEligibility();
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.runCustomDiscountScript();
    });

    this.runCustomDiscountScript();
    this.bindGiftProductEvents();
  }

  /**
   * Handle cart change events
   */
  async handleCartChange() {
    // Skip if we're on cart page
    if (window.location.pathname === '/cart') {
      // Still update cart count but don't check gift eligibility
      this.updateCartCount();
      return;
    }

    // Update cart count immediately
    this.updateCartCount();
    
    // Check gift eligibility when cart changes (removals, quantity changes)
    setTimeout(() => {
      this.checkGiftEligibility();
    }, 200);
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.removeEventListener('on:cart:change', this.cartChangeHandler);
    // Note: on:cart:add listener is anonymous so it will be cleaned up automatically
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
    this.runCustomDiscountScript();
    
    // Update cart count in header
    this.updateCartCount();
    
    // Reset gift refresh flag when cart is refreshed normally
    this.hasTriedGiftRefresh = false;
    
    // Skip gift eligibility check if we're on cart page
    if (window.location.pathname === '/cart') {
      return;
    }
    
    // Only check gift eligibility if not already checking
    if (!this.isCheckingGiftEligibility) {
      setTimeout(() => {
        this.checkGiftEligibility();
      }, 100);
    }
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }

  /**
   * Bind gift product events
   */
  bindGiftProductEvents() {
    // Remove existing listeners to avoid duplicates
    this.removeEventListener('click', this.giftClickHandler);
    
    // Create bound handler if it doesn't exist
    if (!this.giftClickHandler) {
      this.giftClickHandler = (event) => {
        if (event.target.classList.contains('gift-add-btn')) {
          this.handleGiftProductAdd(event);
        }
      };
    }
    
    // Add the event listener
    this.addEventListener('click', this.giftClickHandler);
  }

  /**
   * Handle adding gift product to cart
   * @param {Event} event - Click event
   */
  async handleGiftProductAdd(event) {
    event.preventDefault();
    
    const btn = event.target;
    const variantId = btn.getAttribute('data-variant-id');
    const originalText = btn.innerHTML;
    
    if (!variantId) {
      console.error('No variant ID found for gift product');
      return;
    }

    // Find the gift container and apply opacity
    const giftContainer = btn.closest('.cart-gift-item');
    if (giftContainer) {
      giftContainer.style.opacity = '0.3';
    }

    // Update button state
    btn.disabled = true;
    btn.classList.add('loading');

    try {
      // Prepare cart add data
      const formData = {
        'items': [{
          'id': parseInt(variantId),
          'quantity': 1,
          'properties': {
            '_gift_item': 'true',
            '_gift_with_purchase': 'true',
            '_original_price': '0',
            '_display_price': 'FREE'
          }
        }]
      };

      // Add to cart via AJAX
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Update the line item to have zero price
      await this.updateGiftItemPrice(data.items);

      // Apply gift discount automatically after adding gift
      await this.applyGiftDiscount();

      // Refresh cart drawer
      this.cartRefreshHandler();

      // Update cart count immediately
      this.updateCartCount();
      
      setTimeout(() => {
        // Hide the gift item since it's now in cart
        const giftContainer = btn.closest('.cart-gift-item');
        if (giftContainer) {
          giftContainer.style.display = 'none';
        }
      }, 1000);

    } catch (error) {
      console.error('Error adding gift to cart:', error);
      
      // Reset button state
      btn.disabled = false;
      btn.innerHTML = originalText;
      btn.classList.remove('loading');
      
      // Show error message
      btn.innerHTML = 'Error - Try Again';
      btn.classList.add('error');
      
      setTimeout(() => {
        btn.innerHTML = originalText;
        btn.classList.remove('error');
      }, 2000);
    }
  }

  /**
   * Update gift item price to zero using line item properties
   * @param {Array} cartItems - Array of cart items
   */
  async updateGiftItemPrice(cartItems) {
    try {
      // Find the gift item that was just added
      const giftItem = cartItems.find(item => 
        item.properties && item.properties._gift_item === 'true'
      );

      if (giftItem) {
        // Update the item with additional properties for frontend display
        const updateData = {
          'id': giftItem.key,
          'quantity': giftItem.quantity,
          'properties': {
            ...giftItem.properties,
            '_display_as_free': 'true',
            '_original_price': giftItem.price.toString()
          }
        };

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        });
      }
    } catch (error) {
      console.error('Error updating gift item price:', error);
    }
  }

  /**
   * Check if cart still qualifies for gift and remove gift if not
   */
  async checkGiftEligibility() {
    // Don't check gift eligibility if we're on cart page
    if (window.location.pathname === '/cart') {
      return;
    }

    // Prevent infinite loops
    if (this.isCheckingGiftEligibility) {
      return;
    }

    try {
      this.isCheckingGiftEligibility = true;
      
      // Get current cart data
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Get threshold from theme setting (in cents)
      const giftThresholdFromSetting = this.getAttribute('data-gift-threshold') || 150; // Default to $150
      const giftThreshold = parseInt(giftThresholdFromSetting) * 100; // Convert dollars to cents
      
      // Check if cart has gift items
      const giftItems = cart.items.filter(item => 
        item.properties && item.properties._gift_item === 'true'
      );
      
      // Calculate subtotal excluding gift items
      const subtotalWithoutGifts = cart.items
        .filter(item => !item.properties || item.properties._gift_item !== 'true')
        .reduce((total, item) => total + item.final_line_price, 0);
      
      // Check if gift option container exists
      const giftContainer = this.querySelector('.cart-gift-item');
      
      if (giftItems.length > 0) {
        // If subtotal is below threshold, remove gift items
        if (subtotalWithoutGifts < giftThreshold) {
          
          // First hide any visible gift containers in the UI
          const giftContainers = this.querySelectorAll('.cart-gift-item');
          giftContainers.forEach(container => {
            container.style.display = 'none';
          });
          
          // Remove gift items from cart
          for (const giftItem of giftItems) {
            await this.removeGiftItem(giftItem.key);
          }
          
          // Reset gift refresh flag to allow it to appear again if conditions are met
          this.hasTriedGiftRefresh = false;
          
          // Refresh cart after all operations - but don't call checkGiftEligibility again
          setTimeout(() => {
            this.querySelector('cart-form').refresh();
            this.runCustomDiscountScript();
            this.updateCartCount();
          }, 300);
        }
      } else if (subtotalWithoutGifts >= giftThreshold) {
        // Cart qualifies for gift but no gift present - show gift option
        this.toggleGiftContainer(true, subtotalWithoutGifts, giftThreshold);
      } else {
        // Cart doesn't qualify for gift - hide gift option
        this.toggleGiftContainer(false, subtotalWithoutGifts, giftThreshold);
      }
    } catch (error) {
      console.error('Error checking gift eligibility:', error);
    } finally {
      this.isCheckingGiftEligibility = false;
    }
  }

  /**
   * Remove a gift item from cart
   * @param {string} itemKey - Cart item key
   */
  async removeGiftItem(itemKey) {
    // Don't remove gift items if we're on cart page
    if (window.location.pathname === '/cart') {
      return;
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          'id': itemKey,
          'quantity': 0
        })
      });

      if (response.ok) {
      }
    } catch (error) {
      console.error('Error removing gift item:', error);
    }
  }

  /**
   * Apply gift discount automatically when gift items are in cart
   */
  async applyGiftDiscount() {
    try {
      
      // Check if there are gift items in cart
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const hasGiftItems = cart.items.some(item => 
        item.properties && item.properties._gift_item === 'true'
      );
      
      if (hasGiftItems) {
        // The discount is now automatic (Buy X Get Y), so we don't need to apply manually
      }
    } catch (error) {
      console.error('Error applying gift discount:', error);
    }
  }

  /**
   * Add CSS styles for gift product states
   */
  addGiftProductStyles() {
    if (document.getElementById('gift-product-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'gift-product-styles';
    style.textContent = `
      .gift-add-btn.loading {
        opacity: 0.7;
        cursor: not-allowed;
      }
      
      .gift-add-btn.error {
        background-color: #dc3545 !important;
        border-color: #dc3545 !important;
      }
      
      .cart-gift-item {
        transition: opacity 0.3s ease;
      }
      
      .cart-gift-item.hiding {
        opacity: 0;
      }
    `;
    document.head.appendChild(style);
  }
  runCustomDiscountScript() {

    if (document.getElementById('apply-discount-btn')) {
      document.getElementById('apply-discount-btn').addEventListener('click', async function () {
        const discountCode = document.querySelector('input[name="discount-test"]').value;
        const spinner = document.getElementById('loading-spinner');
        const discountMessage = document.getElementById('discount-message');

        spinner.style.display = 'block';
        discountMessage.style.display = 'none';
        discountMessage.classList.remove('discount-success', 'discount-error');

        try {
          await fetch(`/discount/${discountCode}`);

          const response = await fetch('/cart.js', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();

            spinner.style.display = 'none';
              console.log('data', data)
            if (data.total_discount > 0) {
              discountMessage.textContent = `Discount code "${discountCode}" has been applied successfully. Total discount: $${(data.total_discount / 100).toFixed(2)}.`;
              discountMessage.classList.add('discount-success');

              document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {bubbles: true}));

            } else {
              discountMessage.textContent = `Discount code "${discountCode}" is not valid or provides no discount.`;
              discountMessage.classList.add('discount-error');
            }

            discountMessage.style.display = 'block';
            console.log('Cart details:', data);
          } else {
            spinner.style.display = 'none';
            alert('Failed to retrieve cart details. Please try again.');
          }
        } catch (error) {
          spinner.style.display = 'none';
          discountMessage.textContent = 'An error occurred while applying the discount code.';
          discountMessage.classList.add('discount-error');
          discountMessage.style.display = 'block';
          console.error('Error:', error);
        }
      });
    }

    const cartContainer = document.querySelector('cart-form');
    cartContainer.addEventListener('click', async function(event) {
      if (event.target.classList.contains('remove-discount-btn') || event.target.closest('.remove-discount-btn')) {
        const button = event.target.closest('.remove-discount-btn');
        const discountCode = button.getAttribute('data-discount');
        const icon = button.querySelector('.remove-discount-icon');
        const spinner = button.querySelector('.loading-spinner');

        button.classList.add('loading');
        icon.style.display = 'none';
        spinner.style.display = 'inline-block';

        try {
          await fetch(`/discount/CLEAR`);

          const response = await fetch('/cart.js', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            console.log('Cart details after discount removal:', data);

            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {bubbles: true}));
          } else {
            throw new Error('Failed to retrieve cart details after clearing discount.');
          }
        } catch (error) {
          console.error('Error:', error);
          alert('There was an error removing the discount. Please try again.');
        } finally {
          button.classList.remove('loading');
          icon.style.display = 'inline-block';
          spinner.style.display = 'none';
        }
      }
    });





  }

  /**
   * Update gift product visibility based on cart eligibility
   * @param {number} subtotal - Current cart subtotal excluding gifts
   * @param {number} threshold - Gift threshold amount
   * @param {boolean} hasGiftInCart - Whether gift is already in cart
   */
  updateGiftProductVisibility(subtotal, threshold, hasGiftInCart) {
    const giftContainer = this.querySelector('.cart-gift-item');
    
    if (!giftContainer) {
      // Gift container doesn't exist, need to refresh to get it if eligible
      if (subtotal >= threshold && !hasGiftInCart) {
        // Only refresh once, and prevent further checking during this refresh
        if (!this.isCheckingGiftEligibility) {
          setTimeout(() => {
            this.querySelector('cart-form').refresh();
            this.runCustomDiscountScript();
            this.updateCartCount();
          }, 100);
        }
      }
      return;
    }
    
    // Show/hide existing gift container immediately
    if (subtotal >= threshold && !hasGiftInCart) {
      giftContainer.style.display = 'block';
    } else {
      giftContainer.style.display = 'none';
    }
  }

  /**
   * Create gift product container dynamically
   */
  async createGiftProductContainer() {
    try {
      
      // Get section HTML to extract gift product info
      const sectionId = this.getAttribute('data-section-id');
      const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch section: ${response.status}`);
      }
      
      const html = await response.text();
      
      // Create a temporary container to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Find the gift product container in the fetched HTML
      const giftContainer = tempDiv.querySelector('.cart-gift-item');
      
      if (giftContainer) {
        // Find the cart items container
        const cartItemsContainer = this.querySelector('.cart-item-list__body');
        
        if (cartItemsContainer) {
          // Remove any existing gift container first
          const existingGift = cartItemsContainer.querySelector('.cart-gift-item');
          if (existingGift) {
            existingGift.remove();
          }
          
          // Append the gift container to the cart items
          cartItemsContainer.appendChild(giftContainer);
          
          // Re-bind event listeners for the new gift button
          this.bindGiftProductEvents();
        } else {
          console.error('Cart items container not found');
        }
      } else {
        console.warn('Gift container not found in section HTML - gift product may not be configured');
      }
    } catch (error) {
      console.error('Error creating gift product container:', error);
    }
  }

  /**
   * Update cart count in header/navigation
   */
  updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update cart count elements
        const cartCountElements = document.querySelectorAll('.cart-link__count, .cart-count, [data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
        });
        
        // Also update any cart badge elements
        const cartBadgeElements = document.querySelectorAll('.cart-badge, .header-cart-count');
        cartBadgeElements.forEach(element => {
          element.textContent = cart.item_count;
          
          // Show/hide badge based on count
          if (cart.item_count > 0) {
            element.style.display = '';
          } else {
            element.style.display = 'none';
          }
        });
      })
      .catch(error => {
        console.error('Error updating cart count:', error);
      });
  }

  /**
   * Show or hide gift container based on eligibility
   * @param {boolean} shouldShow - Whether to show the gift container
   * @param {number} subtotal - Current subtotal
   * @param {number} threshold - Threshold amount
   */
  toggleGiftContainer(shouldShow, subtotal, threshold) {
    // Don't toggle gift container if we're on cart page
    if (window.location.pathname === '/cart') {
      return;
    }
    
    const giftContainer = this.querySelector('.cart-gift-item');
    
    if (shouldShow) {
      if (!giftContainer) {
        // Need to fetch and add gift container
        if (!this.hasTriedGiftRefresh) {
          this.hasTriedGiftRefresh = true;
          this.refreshEntireSection();
        }
      } else {
        // Show existing container
        giftContainer.style.display = 'block';
      }
    } else {
      if (giftContainer) {
        giftContainer.style.display = 'none';
        giftContainer.remove();
      }
    }
  }

  /**
   * Refresh the entire cart drawer section to ensure gift product appears
   */
  async refreshEntireSection() {
    // Don't refresh if we're on the cart page to prevent conflicts
    if (window.location.pathname === '/cart') {
      return;
    }
    
    try {
      
      const sectionId = this.getAttribute('data-section-id');
      if (!sectionId) {
        console.error('No section ID found for cart drawer');
        return;
      }

      // Fetch the entire section HTML
      const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch section: ${response.status}`);
      }
      
      const sectionHtml = await response.text();
      
      // Create a temporary container to parse the new HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sectionHtml;
      
      // Find the gift product container in the new HTML
      const newGiftContainer = tempDiv.querySelector('.cart-gift-item');
      
      if (newGiftContainer) {
        
        // Remove any existing gift container first
        const existingGift = this.querySelector('.cart-gift-item');
        if (existingGift) {
          existingGift.remove();
        }
        
        // Find where to insert the gift product (typically in the cart items area)
        const cartItemsContainer = this.querySelector('.cart-item-list__body, .cart-items, .drawer__body');
        
        if (cartItemsContainer) {
          // Insert the gift container at the end of cart items
          cartItemsContainer.appendChild(newGiftContainer);
          
          // Re-bind event listeners for the new gift button
          this.bindGiftProductEvents();
        } else {
          console.error('Cart items container not found');
        }
      }
    } catch (error) {
      console.error('Error refreshing section for gift product:', error);
      
      // Don't fallback to full refresh as it causes image issues
    }
  }

}

window.customElements.define('cart-drawer', CartDrawer);
